import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Tabs, Tab, Typography, Box, Chip, Button, Switch, TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { Close } from '@mui/icons-material';
import { horizontalTabStyles } from '../sharedStyles';
import axios from '../../../common/AxiosConfig';
import { AppContext } from '../../../../RoutesWithAuth';
import { ADMIN_PERMISSIONS } from '../../../common/RolePermissions';
import PermissionButton from '../PermissionButton';

const useStyles = makeStyles(() => ({
    frequencyTab: {
        height: '100%',
        ...horizontalTabStyles.tabWrapper,
        '& .header': {
            padding: '10px 20px',
            fontSize: '1rem',
            fontWeight: 'bold'
        },
    },
    tabBody: {
        ...horizontalTabStyles.tabBody,
        padding: '24px 28px',
    },
    scheduleCard: {
        border: '1px solid #edf2f7',
        borderRadius: '10px',
        padding: '28px 32px',
        marginBottom: '20px',
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(30, 58, 95, 0.06)',
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.95rem',
        color: '#1e3a5f',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #267db7',
        letterSpacing: '0.3px',
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '0.8rem',
        color: '#1e3a5f',
        marginBottom: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    fieldValue: {
        background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)',
        borderRadius: '6px',
        padding: '11px 16px',
        fontSize: '0.85rem',
        color: '#2f4f4f',
        marginBottom: '20px',
        border: '1px solid #d6e8f7',
        fontWeight: 500,
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    requestorChip: {
        fontSize: '0.8rem',
        fontWeight: 500,
        borderColor: '#267db7',
        color: '#1e3a5f',
        background: '#f0f7ff',
    },
    fieldSection: {
        paddingBottom: '18px',
        marginBottom: '18px',
        borderBottom: '1px solid #edf2f7',
    },
    fieldSectionLast: {
        marginBottom: '0',
    },
    selectHeaderRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },
    hoursGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '10px',
        marginBottom: '20px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #edf2f7',
    },
    hourChip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 4px',
        borderRadius: '6px',
        fontSize: '0.78rem',
        fontWeight: 500,
        cursor: 'pointer',
        border: '1.5px solid #cbd5e1',
        background: '#ffffff',
        color: '#475569',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        '&:hover': {
            borderColor: '#267db7',
            background: '#f0f7ff',
        },
    },
    hourChipSelected: {
        background: 'linear-gradient(135deg, #267db7 0%, #3b9edd 100%)',
        color: '#ffffff',
        border: '1.5px solid #267db7',
        fontWeight: 600,
        boxShadow: '0 2px 4px rgba(38, 125, 183, 0.25)',
        '&:hover': {
            background: 'linear-gradient(135deg, #1e6aa6 0%, #2a8acc 100%)',
            borderColor: '#1e6aa6',
        },
    },
    hourChipDisabled: {
        cursor: 'default',
        opacity: 0.7,
        '&:hover': {
            borderColor: '#cbd5e1',
            background: '#ffffff',
        },
    },
    hourChipDisabledSelected: {
        cursor: 'default',
        '&:hover': {
            background: 'linear-gradient(135deg, #267db7 0%, #3b9edd 100%)',
            borderColor: '#267db7',
        },
    },
    daysRow: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '20px',
        padding: '14px 16px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #edf2f7',
        alignItems: 'center',
    },
    dayToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: '#1e3a5f',
        padding: '4px 8px',
        borderRadius: '6px',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        transition: 'all 0.2s ease',
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#267db7',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#267db7',
        },
    },
    selectActions: {
        display: 'flex',
        gap: '16px',
    },
    selectLink: {
        cursor: 'pointer',
        color: '#267db7',
        fontSize: '0.78rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#1e3a5f',
            textDecoration: 'underline',
        },
    },
    summaryBox: {
        background: 'linear-gradient(135deg, #e8f4fd 0%, #dbeafe 100%)',
        borderRadius: '8px',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '20px',
        fontSize: '0.82rem',
        color: '#1e3a5f',
        fontWeight: 500,
        border: '1px solid #bfdbfe',
    },
    summaryIcon: {
        background: 'linear-gradient(135deg, #267db7 0%, #3b9edd 100%)',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    buttonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #edf2f7',
    },
    saveButton: {
        padding: '6px 28px',
        fontSize: '0.75rem',
        textTransform: 'none',
        fontWeight: 600,
        background: 'linear-gradient(180deg, #3b9edd 0%, #267db7 54%, #1e3a5f 125%)',
        color: '#ffffff',
        borderRadius: '6px',
        letterSpacing: '0.3px',
        '&:hover': {
            background: 'linear-gradient(180deg, #2a8acc 0%, #1e6aa6 54%, #162d4e 125%)',
        },
        '&:disabled': {
            background: '#cbd5e1',
            color: '#94a3b8',
        },
    },
    cancelButton: {
        padding: '6px 28px',
        fontSize: '0.75rem',
        textTransform: 'none',
        fontWeight: 600,
        background: '#ffffff',
        color: '#1e3a5f',
        borderRadius: '6px',
        border: '1px solid #cbd5e1',
        letterSpacing: '0.3px',
        '&:hover': {
            background: '#f8fafc',
            borderColor: '#94a3b8',
        },
    },
    escalateButton: {
        padding: '6px 28px',
        fontSize: '0.75rem',
        textTransform: 'none',
        fontWeight: 600,
        background: '#ffffff',
        color: '#267db7',
        borderRadius: '6px',
        border: '1px solid #267db7',
        letterSpacing: '0.3px',
        '&:hover': {
            background: '#f0f7ff',
            borderColor: '#1e6aa6',
        },
        '&:disabled': {
            background: '#ffffff',
            color: '#94a3b8',
            borderColor: '#cbd5e1',
        },
    },
    timezoneField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: 500,
            background: '#f8fafc',
        },
    },
    confirmDialogPaper: {
        borderRadius: '10px',
        width: '480px',
        maxWidth: '480px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(30, 58, 95, 0.25)',
    },
    confirmDialogTitle: {
        background: 'linear-gradient(135deg, #1e3a5f 0%, #267db7 125%, #3b9edd 100%)',
        color: '#fff',
        padding: '8px 20px !important',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
    },
    confirmDialogClose: {
        color: '#fff',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '50%',
    },
    confirmDialogContent: {
        padding: '24px 20px !important',
        fontSize: '0.88rem',
        color: '#2f4f4f',
        lineHeight: 1.6,
        background: '#ffffff',
    },
    confirmDialogActions: {
        padding: '14px 20px',
        borderTop: '1px solid #edf2f7',
        justifyContent: 'flex-end',
        gap: '12px',
    },
}));

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_LABELS = [
    '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM',
    '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
    '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM',
    '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM',
];
const DAY_NUM_TO_NAME = {
    1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
    5: 'Friday', 6: 'Saturday', 7: 'Sunday',
};
const DAYS = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' },
];

const EscalationFrequency = ({ active }) => {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // holds the run frequency (DAILY/WEEKLY) awaiting confirmation, null when the dialog is closed
    const [escalateFrequency, setEscalateFrequency] = useState(null);
    const [escalating, setEscalating] = useState(false);
    const alertAnchorRef = useRef(null);

    // Daily state
    const [dailyHours, setDailyHours] = useState([]);
    const [dailyRequestors, setDailyRequestors] = useState('');

    // Weekly state
    const [weeklyDays, setWeeklyDays] = useState([]);
    const [weeklyRequestors, setWeeklyRequestors] = useState('');
    const [weeklyTimeOfDay, setWeeklyTimeOfDay] = useState('08:00 AM');

    // Original state for cancel/reset
    const [originalDailyHours, setOriginalDailyHours] = useState([]);
    const [originalWeeklyDays, setOriginalWeeklyDays] = useState([]);

    const FrequencyTabs = ["daily", "weekly"];

    useEffect(() => {
        if (active) {
            const tabIndex = FrequencyTabs.indexOf(active);
            if (tabIndex >= 0) setActiveTab(tabIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const fetchData = useCallback(() => {
        setLoading(true);
        axios.get('settings/escalation-frequency', {
            headers: { Authorization: authToken }
        }).then((response) => {
            const data = response.data;

            // Daily
            if (data.daily) {
                setDailyRequestors(data.daily.requestor_details.join(', '));
                const hours = data.daily.selected_values.map(Number);
                setDailyHours(hours);
                setOriginalDailyHours(hours);
            }

            // Weekly
            if (data.weekly) {
                setWeeklyRequestors(data.weekly.requestor_details.join(', '));
                const days = data.weekly.selected_values.map(v => {
                    const num = Number(v);
                    return isNaN(num) ? v : (DAY_NUM_TO_NAME[num] || v);
                });
                setWeeklyDays(days);
                setOriginalWeeklyDays(days);
                if (data.weekly.time_of_day) {
                    setWeeklyTimeOfDay(data.weekly.time_of_day);
                }
            }
            setLoading(false);
        }).catch(() => {
            setError('Failed to load escalation frequency settings');
            setLoading(false);
        });
    }, [authToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (error || success) {
            alertAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const handleSaveDaily = () => {
        if (dailyHours.length === 0) {
            setError('Please select at least one hour');
            return;
        }
        setSaving(true);
        axios.put('settings/escalation-frequency', {
            run_frequency: 'DAILY',
            selected_values: dailyHours.sort((a, b) => a - b)
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            setOriginalDailyHours([...dailyHours]);
            setSuccess('Daily schedule saved successfully');
            setSaving(false);
        }).catch(() => {
            setError('Failed to save daily schedule');
            setSaving(false);
        });
    };

    const handleSaveWeekly = () => {
        if (weeklyDays.length === 0) {
            setError('Please select at least one day');
            return;
        }
        setSaving(true);
        axios.put('settings/escalation-frequency', {
            run_frequency: 'WEEKLY',
            selected_values: DAYS.filter(d => weeklyDays.includes(d.value)).map(d => d.value)
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            setOriginalWeeklyDays([...weeklyDays]);
            setSuccess('Weekly schedule saved successfully');
            setSaving(false);
        }).catch(() => {
            setError('Failed to save weekly schedule');
            setSaving(false);
        });
    };

    const handleEscalateNowConfirm = () => {
        const runFrequency = escalateFrequency;
        setEscalateFrequency(null);
        setEscalating(true);
        axios.post('settings/escalation-frequency/escalate-now', {
            run_frequency: runFrequency
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            setSuccess(`Escalation emails triggered successfully for the ${runFrequency.toLowerCase()} schedule`);
            setEscalating(false);
        }).catch(() => {
            setError('Failed to trigger escalation emails');
            setEscalating(false);
        });
    };

    const handleCancelDaily = () => {
        setDailyHours([...originalDailyHours]);
    };

    const handleCancelWeekly = () => {
        setWeeklyDays([...originalWeeklyDays]);
    };

    const toggleHour = (hour) => {
        setDailyHours(prev =>
            prev.includes(hour) ? prev.filter(h => h !== hour) : [...prev, hour]
        );
    };

    const toggleDay = (day) => {
        setWeeklyDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        );
    };

    const getDailySummary = () => {
        const sorted = [...dailyHours].sort((a, b) => a - b);
        const labels = sorted.map(h => HOUR_LABELS[h]);
        return `Job runs at ${labels.join(', ')} every day (CST)`;
    };

    const getWeeklySummary = () => {
        const ordered = DAYS.filter(d => weeklyDays.includes(d.value)).map(d => d.value);
        if (ordered.length === 0) return '';
        const last = ordered[ordered.length - 1];
        const rest = ordered.slice(0, -1);
        const dayStr = rest.length > 0 ? `${rest.join(', ')} and ${last}` : last;
        return `Job runs at ${weeklyTimeOfDay} (CST) every ${dayStr}`;
    };

    const renderDailyTab = () => (
        <div className={classes.scheduleCard}>
            <Typography className={classes.sectionTitle}>Daily Run Schedule</Typography>

            <div className={classes.fieldSection}>
                <div className={classes.fieldLabel}>Requestor Details</div>
                <div className={classes.chipRow}>
                    {(dailyRequestors || 'Driver, Survivor').split(', ').map(r => (
                        <Chip key={r} label={r} variant="outlined" className={classes.requestorChip} />
                    ))}
                </div>
            </div>

            <div className={classes.fieldSection}>
                <div className={classes.fieldLabel}>Run Frequency</div>
                <div className={classes.fieldValue} style={{ marginBottom: 0 }}>Daily - Multiple Hours</div>
            </div>

            <div className={classes.fieldSection}>
                <div className={classes.selectHeaderRow}>
                    <div className={classes.fieldLabel} style={{ marginBottom: 0 }}>Select Hours to Run</div>
                    {isAdmin && (
                        <div className={classes.selectActions}>
                            <span className={classes.selectLink} onClick={() => setDailyHours(HOURS.slice())}>Select All</span>
                            <span className={classes.selectLink} onClick={() => setDailyHours([])}>Clear All</span>
                        </div>
                    )}
                </div>
                <div className={classes.hoursGrid}>
                    {HOURS.map(hour => {
                        const selected = dailyHours.includes(hour);
                        const chipClass = [
                            classes.hourChip,
                            selected ? classes.hourChipSelected : '',
                            !isAdmin ? (selected ? classes.hourChipDisabledSelected : classes.hourChipDisabled) : '',
                        ].join(' ');
                        return (
                            <div
                                key={hour}
                                className={chipClass}
                                onClick={() => isAdmin && toggleHour(hour)}
                            >
                                {HOUR_LABELS[hour]}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className={classes.fieldLabel}>Timezone</div>
                <TextField
                    size="small"
                    value="CST"
                    disabled
                    variant="outlined"
                    className={classes.timezoneField}
                    sx={{ width: '200px', marginBottom: '16px' }}
                />
            </div>

            {dailyHours.length > 0 && (
                <div className={classes.summaryBox}>
                    <span className={classes.summaryIcon}>i</span>
                    <span>Schedule Summary: {getDailySummary()}</span>
                </div>
            )}


                <div className={classes.buttonRow}>
                    <PermissionButton permission={ADMIN_PERMISSIONS.ESC_FREQ_DAILY_ESC} variant="outlined" className={classes.escalateButton} onClick={() => setEscalateFrequency('DAILY')} disabled={saving || escalating}>
                        Escalate Now
                    </PermissionButton>
                    <PermissionButton permission={ADMIN_PERMISSIONS.ESC_FREQ_DAILY_SAVE} variant="contained" className={classes.saveButton} onClick={handleSaveDaily} disabled={saving}>
                        Save
                    </PermissionButton>
                    <Button variant="outlined" className={classes.cancelButton} onClick={handleCancelDaily} disabled={saving}>
                        Cancel
                    </Button>
                </div>

        </div>
    );

    const renderWeeklyTab = () => (
        <div className={classes.scheduleCard}>
            <Typography className={classes.sectionTitle}>Weekly Run Schedule</Typography>

            <div className={classes.fieldSection}>
                <div className={classes.fieldLabel}>Requestor Details</div>
                <div className={classes.chipRow}>
                    {(weeklyRequestors || 'B2B Users, Customer, Employee, TCI Customer').split(', ').map(r => (
                        <Chip key={r} label={r} variant="outlined" className={classes.requestorChip} />
                    ))}
                </div>
            </div>

            <div className={classes.fieldSection}>
                <div className={classes.fieldLabel}>Run Frequency</div>
                <div className={classes.fieldValue} style={{ marginBottom: 0 }}>Weekly - Multiple Days</div>
            </div>

            <div className={classes.fieldSection}>
                <div className={classes.fieldLabel}>Select Days to Run</div>
                <div className={classes.daysRow}>
                    {DAYS.map(day => (
                        <div key={day.value} className={classes.dayToggle}>
                            <span>{day.label}</span>
                            <Switch
                                size="small"
                                checked={weeklyDays.includes(day.value)}
                                onChange={() => toggleDay(day.value)}
                                disabled={!isAdmin}
                                color="primary"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <Box display="flex" gap={2} mb={2}>
                    <div>
                        <div className={classes.fieldLabel}>Time of Day</div>
                        <TextField
                            size="small"
                            value={weeklyTimeOfDay}
                            disabled
                            variant="outlined"
                            className={classes.timezoneField}
                            sx={{ width: '200px' }}
                        />
                    </div>
                    <div>
                        <div className={classes.fieldLabel}>Timezone</div>
                        <TextField
                            size="small"
                            value="CST"
                            disabled
                            variant="outlined"
                            className={classes.timezoneField}
                            sx={{ width: '200px' }}
                        />
                    </div>
                </Box>
            </div>

            {weeklyDays.length > 0 && (
                <div className={classes.summaryBox}>
                    <span className={classes.summaryIcon}>i</span>
                    <span>Schedule Summary: {getWeeklySummary()}</span>
                </div>
            )}


                <div className={classes.buttonRow}>
                    <PermissionButton permission={ADMIN_PERMISSIONS.ESC_FREQ_WEEKLY_ESC} variant="outlined" className={classes.escalateButton} onClick={() => setEscalateFrequency('WEEKLY')} disabled={saving || escalating}>
                        Escalate Now
                    </PermissionButton>
                    <PermissionButton permission={ADMIN_PERMISSIONS.ESC_FREQ_WEEKLY_SAVE} variant="contained" className={classes.saveButton} onClick={handleSaveWeekly} disabled={saving}>
                        Save
                    </PermissionButton>
                    <Button variant="outlined" className={classes.cancelButton} onClick={handleCancelWeekly} disabled={saving}>
                        Cancel
                    </Button>
                </div>

        </div>
    );

    if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

    return (
        <div className={classes.frequencyTab}>
            <div ref={alertAnchorRef} />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Typography variant="h6" className='header'>
                FREQUENCY
            </Typography>
            <Tabs
                variant="scrollable"
                aria-label="frequency tabs"
                value={activeTab}
                onChange={(_event, newValue) => {
                    setActiveTab(newValue);
                }}
                sx={{ borderColor: 'divider' }}
            >
                <Tab label="DAILY" />
                <Tab label="WEEKLY" />
            </Tabs>
            <div className={classes.tabBody}>
                {activeTab === 0 && renderDailyTab()}
                {activeTab === 1 && renderWeeklyTab()}
            </div>
            {escalateFrequency && (
                <Dialog
                    open={true}
                    onClose={() => setEscalateFrequency(null)}
                    classes={{ paper: classes.confirmDialogPaper }}
                >
                    <DialogTitle className={classes.confirmDialogTitle} disableTypography>
                        <span>Confirm Escalate Now</span>
                        <button
                            type="button"
                            aria-label="close"
                            className={classes.confirmDialogClose}
                            onClick={() => setEscalateFrequency(null)}
                        >
                            <Close fontSize="small" />
                        </button>
                    </DialogTitle>
                    <DialogContent className={classes.confirmDialogContent}>
                        Are you sure you want to send escalation emails to all downstreams with responses pending beyond the threshold?
                    </DialogContent>
                    <DialogActions className={classes.confirmDialogActions}>
                        <Button variant="contained" className={classes.saveButton} onClick={handleEscalateNowConfirm}>
                            Yes
                        </Button>
                        <Button variant="outlined" className={classes.cancelButton} onClick={() => setEscalateFrequency(null)}>
                            No
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default EscalationFrequency;
